const configuredBaseUrl =
  process.env.REACT_APP_API_BASE_URL || "https://api-ai.e-slip-sjs.com/";

function normalizeApiBaseUrl(value) {
  const trimmed = String(value || "").replace(/\/+$/, "");
  if (trimmed.endsWith("/api/v1")) return trimmed;
  return `${trimmed}/api/v1`;
}

export const API_BASE_URL = normalizeApiBaseUrl(configuredBaseUrl);

const ACCESS_TOKEN_KEY = "internal_ai_access_token";
const REFRESH_TOKEN_KEY = "internal_ai_refresh_token";
const USER_KEY = "internal_ai_user";

export function getStoredAuth() {
  const userRaw = localStorage.getItem(USER_KEY);
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
    user: userRaw ? JSON.parse(userRaw) : null,
  };
}

export function storeAuth(data) {
  if (data.access_token) localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  if (data.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
}

export function updateStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function getErrorMessage(payload, fallback) {
  return payload?.error?.message || payload?.detail || payload?.message || fallback;
}

function shouldRefresh(path) {
  return !["/auth/login", "/auth/refresh", "/auth/logout"].includes(path);
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error("Session berakhir. Silakan login ulang.");

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    clearAuth();
    throw new Error(getErrorMessage(payload, "Session berakhir. Silakan login ulang."));
  }
  storeAuth(payload);
  return payload.access_token;
}

export async function apiRequest(path, options = {}, retry = true) {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const headers = new Headers(options.headers || {});
  const isForm = options.body instanceof FormData;

  if (!isForm && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  if (response.status === 401 && retry && shouldRefresh(path)) {
    const nextToken = await refreshAccessToken();
    headers.set("Authorization", `Bearer ${nextToken}`);
    return apiRequest(path, { ...options, headers }, false);
  }

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json().catch(() => ({}))
    : await response.blob();

  if (!response.ok || payload?.success === false) {
    throw new Error(getErrorMessage(payload, "Request gagal"));
  }
  return payload;
}

export const api = {
  login: (body) =>
    apiRequest("/auth/login", { method: "POST", body: JSON.stringify(body) }),
  bootstrapSuperAdmin: (body) =>
    apiRequest("/auth/bootstrap-superadmin", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  me: () => apiRequest("/auth/me"),
  logout: (refreshToken) =>
    apiRequest("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),

  chatSessions: () => apiRequest("/chat/sessions"),
  searchChats: (q, limit = 20) =>
    apiRequest(`/chat/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  createChatSession: (body = {}) =>
    apiRequest("/chat/sessions", { method: "POST", body: JSON.stringify(body) }),
  chatMessages: (sessionId) => apiRequest(`/chat/sessions/${sessionId}/messages`),
  sendChatMessage: (sessionId, body) =>
    apiRequest(`/chat/sessions/${sessionId}/messages`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  archiveChatSession: (sessionId) =>
    apiRequest(`/chat/sessions/${sessionId}/archive`, { method: "PATCH" }),
  deleteChatSession: (sessionId) =>
    apiRequest(`/chat/sessions/${sessionId}`, { method: "DELETE" }),
  feedback: (messageId, body) =>
    apiRequest(`/chat/messages/${messageId}/feedback`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  collections: () => apiRequest("/knowledge/collections"),
  createCollection: (body) =>
    apiRequest("/knowledge/collections", { method: "POST", body: JSON.stringify(body) }),
  updateCollection: (collectionId, body) =>
    apiRequest(`/knowledge/collections/${collectionId}`, { method: "PATCH", body: JSON.stringify(body) }),
  collectionDocuments: (collectionId) =>
    apiRequest(`/knowledge/collections/${collectionId}/documents`),
  uploadDocument: (formData) =>
    apiRequest("/knowledge/documents", { method: "POST", body: formData }),
  documentDetail: (documentId) => apiRequest(`/knowledge/documents/${documentId}`),
  updateDocument: (documentId, body) =>
    apiRequest(`/knowledge/documents/${documentId}`, { method: "PATCH", body: JSON.stringify(body) }),
  updateDocumentWithFile: (documentId, formData) =>
    apiRequest(`/knowledge/documents/${documentId}/with-file`, { method: "PATCH", body: formData }),
  reingestDocument: (documentId) =>
    apiRequest(`/knowledge/documents/${documentId}/reingest`, { method: "POST" }),
  archiveDocument: (documentId) =>
    apiRequest(`/knowledge/documents/${documentId}/archive`, { method: "PATCH" }),
  createAccessRule: (body) =>
    apiRequest("/knowledge/access-rules", { method: "POST", body: JSON.stringify(body) }),
  searchKnowledgePrincipals: (principalType, q = "") =>
    apiRequest(`/knowledge/principals?principal_type=${encodeURIComponent(principalType)}&q=${encodeURIComponent(q)}`),

  createVoiceSession: (body = {}) =>
    apiRequest("/voice/sessions", { method: "POST", body: JSON.stringify(body) }),
  voiceTurns: (voiceSessionId) => apiRequest(`/voice/sessions/${voiceSessionId}/turns`),
  sendVoiceTurn: (voiceSessionId, formData) =>
    apiRequest(`/voice/sessions/${voiceSessionId}/turns`, {
      method: "POST",
      body: formData,
    }),

  auditLogs: () => apiRequest("/audit/logs"),
  dataAccessLogs: () => apiRequest("/audit/data-access"),

  qwenVlDebugStatus: () => apiRequest("/debug/qwen-vl"),
  qwenVlSampleDocuments: (collectionCode = "SURAT_EDARAN") =>
    apiRequest(`/debug/qwen-vl/sample-documents?collection_code=${encodeURIComponent(collectionCode)}`),
  validateQwenVlSample: ({ documentId, collectionCode = "SURAT_EDARAN", force = true, question } = {}) => {
    const params = new URLSearchParams();
    if (documentId) params.set("document_id", documentId);
    if (collectionCode) params.set("collection_code", collectionCode);
    params.set("force", force ? "true" : "false");
    if (question) params.set("question", question);
    return apiRequest(`/debug/qwen-vl/validate-sample?${params.toString()}`, { method: "POST" });
  },

  // Users management
  listUsers: (limit = 100, offset = 0) =>
    apiRequest(`/users?limit=${limit}&offset=${offset}`),
  updateMyProfile: (body) =>
    apiRequest("/users/me", { method: "PATCH", body: JSON.stringify(body) }),
  createUser: (body) =>
    apiRequest("/users", { method: "POST", body: JSON.stringify(body) }),
  getUser: (userId) => apiRequest(`/users/${userId}`),
  updateUser: (userId, body) =>
    apiRequest(`/users/${userId}`, { method: "PATCH", body: JSON.stringify(body) }),
  assignRole: (userId, body) =>
    apiRequest(`/users/${userId}/roles`, { method: "POST", body: JSON.stringify(body) }),
  removeRole: (userId, roleId) =>
    apiRequest(`/users/${userId}/roles/${roleId}`, { method: "DELETE" }),

  // Roles & permissions
  listRoles: () => apiRequest("/roles"),
  createRole: (body) =>
    apiRequest("/roles", { method: "POST", body: JSON.stringify(body) }),
  updateRole: (roleId, body) =>
    apiRequest(`/roles/${roleId}`, { method: "PATCH", body: JSON.stringify(body) }),
  getRolePermissions: (roleId) => apiRequest(`/roles/${roleId}/permissions`),
  listPermissions: () => apiRequest("/permissions"),
  setRolePermissions: (roleId, body) =>
    apiRequest(`/roles/${roleId}/permissions`, { method: "POST", body: JSON.stringify(body) }),

  // Knowledge access rules
  deleteAccessRule: (ruleId) =>
    apiRequest(`/knowledge/access-rules/${ruleId}`, { method: "DELETE" }),
};
